import style from '@/less/main.less';
import { useStores } from '@/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorMessage, Formik } from 'formik';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { Button, Form, FormField, FormGroup, Icon, Modal, TextArea } from 'semantic-ui-react';
import { object, string } from 'yup';
import Bulletin from './Bulletin';

interface TriggerOnboardingModalProps {
  userId: number;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}

export const TriggerOnboardingModal = observer(
  ({ userId, isOpen, onClose, onSubmit }: TriggerOnboardingModalProps) => {
    const { userStore } = useStores();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleClose = () => {
      setErrorMessage(null);
      onClose();
    };

    return (
      <Modal
        className={style.modal}
        open={isOpen}
        size="tiny"
        closeIcon={<Icon name="close" size="tiny" color="black" />}
        closeOnDimmerClick={false}
        onClose={handleClose}
      >
        <Formik
          initialValues={{ notes: '' }}
          validationSchema={object().shape({
            notes: string().notRequired(),
          })}
          onSubmit={async ({ notes }) => {
            try {
              await userStore.triggerOnboarding(userId, notes);

              await onSubmit();
            } catch (error) {
              console.error('error triggering onboarding', error);

              // @ts-expect-error
              if ('message' in error) {
                // @ts-expect-error
                setErrorMessage(error.message);
                return;
              }

              throw error;
            }
          }}
        >
          {({ values, setFieldValue, handleSubmit, handleBlur }) => (
            <>
              <Modal.Header>Trigger Onboarding</Modal.Header>
              <Modal.Content>
                {errorMessage && (
                  <Bulletin
                    errorMessage={errorMessage}
                    isLoading={false}
                    handleDismiss={() => setErrorMessage(null)}
                  />
                )}

                <Modal.Description style={{ marginBottom: 18 }}>
                  You may enter a note to send to the employee to explain why they need to redo the
                  onboarding.
                </Modal.Description>

                <Form>
                  <FormGroup>
                    <FormField width={16}>
                      <label htmlFor="notes">Notes</label>
                      <TextArea
                        id="notes"
                        value={values.notes}
                        placeholder="Notes here..."
                        onChange={(_, data) => setFieldValue('notes', data.value)}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage
                        name="notes"
                        render={(err) => <span style={{ color: 'red', fontSize: 13 }}>{err}</span>}
                      />
                    </FormField>
                  </FormGroup>
                </Form>
              </Modal.Content>

              <Modal.Actions>
                <Button color="grey" basic style={{ fontSize: '0.9rem' }} onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="green"
                  style={{ fontSize: '0.9rem' }}
                  onClick={() => handleSubmit()}
                >
                  <FontAwesomeIcon icon={['fas', 'user-plus']} style={{ marginRight: 8 }} />
                  Trigger Onboarding
                </Button>
              </Modal.Actions>
            </>
          )}
        </Formik>
      </Modal>
    );
  },
);
